import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../Common/Configurations/AuthContext";
import config from "../../../Common/Configurations/APIConfig";
import apiService from "../../../Common/Configurations/APIService";
import {
  RiArrowLeftLine,
  RiBusLine,
  RiUserLine,
  RiMapPinLine,
  RiShieldLine,
  RiTimeLine,
  RiLockUnlockLine,
  RiAlertFill,
} from "react-icons/ri";
import styles from "./BusPayment2.module.css";

const BusPayment2 = ({ mode, onNavigate, templateId, params, styleConfig }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const themeVariables = {
    "--primary-color": styleConfig?.primary || "#1a3c2e",
    "--secondary-color": styleConfig?.secondary || "#c9a84c",
  };

  const {
    bus, searchQuery, selectedSeats, passengerCount, passengerDetails, selectedBoardingPoint, selectedDroppingPoint,
  } = params || location.state || {};

  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("RAZORPAY");
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${day}-${monthNames[date.getMonth()]}-${date.getFullYear()}`;
    } catch (error) { return dateString; }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours);
    return `${hour % 12 || 12}:${minutes} ${hour >= 12 ? "PM" : "AM"}`;
  };

  useEffect(() => {
    if (!bus || !searchQuery || !selectedSeats?.length || !passengerDetails?.length) {
      if (mode === "builder") onNavigate({ name: "bus-results" });
      else if (mode === "preview") navigate(`/template-preview/${templateId}/bus-results`);
      else navigate("/template/bus/results");
    }
  }, [bus, searchQuery, selectedSeats, passengerDetails, navigate, mode, onNavigate, templateId]);

  if (!bus || !searchQuery || !selectedSeats?.length || !passengerDetails?.length) {
    return (
      <div className={styles.wrapper} style={themeVariables}>
        <div className={styles.errorContainer}>
          <div className={styles.errorCard}>
            <RiAlertFill className={styles.errorIcon} />
            <h2>Booking Session Expired</h2>
            <p>Your booking details were lost or the session timed out.</p>
            <button 
              className={styles.primaryButton}
              onClick={() => mode === 'preview' ? navigate(`/template-preview/${templateId}/bus-search`) : navigate('/template/bus-search')}
            >
              Go Back to Search
            </button>
          </div>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount) => `₹${Math.round(parseFloat(amount) || 0).toLocaleString("en-IN")}`;

  const calculateBreakdown = () => {
    const base = selectedSeats?.reduce((sum, seat) => sum + (parseFloat(seat.totalPriceWithTaxes) || 0), 0) || 0;
    const convenience = selectedSeats?.reduce((sum, seat) => sum + (parseFloat(seat.convenienceFee) || 100), 0) || 0;
    return { baseFare: base, convenienceFee: convenience, total: base + convenience };
  };

  const buildBookingRequest = () => {
    const breakdown = calculateBreakdown();
    const gst = 18;
    const gstAmount = breakdown.convenienceFee * (gst / 100);

    return {
      CompanyId: user?.cid || "00000000-0000-0000-0000-000000000000",
      BranchId: user?.branchId || "00000000-0000-0000-0000-000000000000",
      UserId: user?.id || "00000000-0000-0000-0000-000000000000",
      CustomerId: user?.customerId || "00000000-0000-0000-0000-000000000000",
      SupplierId: bus.supplierId,
      SourceCity: searchQuery.fromStation,
      DestinationCity: searchQuery.toStation,
      InventoryType: bus.inventoryType,
      RouteScheduleId: bus.routeScheduleId,
      BusType: bus.busType,
      OperatorName: bus.operatorName,
      JourneyType: "ONE_WAY",
      RequestedBy: "CUSTOMER",
      BaseFare: breakdown.baseFare,
      Tax: selectedSeats.reduce((sum, seat) => sum + (parseFloat(seat.serviceTaxAmount) || 0), 0),
      ConvenienceFee: breakdown.convenienceFee,
      GST: gst,
      GSTAmount: gstAmount,
      TotalFare: breakdown.baseFare,
      TotalPrice: breakdown.total,
      Markup: 0,
      CustomerName: `${passengerDetails[0].firstName} ${passengerDetails[0].lastName}`,
      CustomerLastName: passengerDetails[0].lastName || "",
      CustomerEmail: passengerDetails[0].email,
      DOJ: new Date(searchQuery.travelDate),
      BoardingPoint: { Id: selectedBoardingPoint.id, Location: selectedBoardingPoint.location, Time: selectedBoardingPoint.time },
      DroppingPoint: { Id: selectedDroppingPoint.id, Location: selectedDroppingPoint.location, Time: selectedDroppingPoint.time },
      CustomerPhone: passengerDetails[0].phone,
      EmergencyPhNumber: passengerDetails[0].phone,
      CustomerAddress: "Not provided",
      IsHold: false,
      BookingStatus: "BOOKED",
      PaymentStatus: "SUCCESS",
      PaymentGatewayAmount: breakdown.total,
      WalletAmount: 0,
      CreditAmount: 0,
      PaymentGateway: paymentMethod,
      CouponCode: "",
      BookSeatPaxDetails: selectedSeats.map((seat, index) => {
        const passenger = passengerDetails[index] || passengerDetails[0];
        return {
          Age: passenger.age,
          Name: `${passenger.firstName} ${passenger.lastName}`,
          SeatNbr: seat.id,
          Sex: passenger.title === "Ms" || passenger.title === "Mrs" ? "F" : "M",
          Fare: parseFloat(seat.totalPriceWithTaxes) || 0,
          ServiceTaxAmount: seat.serviceTaxAmount || 0,
          OperatorServiceChargeAbsolute: parseFloat(seat.convenienceFee) || 100,
          TotalFareWithTaxes: parseFloat(seat.totalPriceWithTaxes) || 0,
          LadiesSeat: seat.ladiesSeat || false,
          LastName: passenger.lastName || "",
          Mobile: passenger.phone,
          Title: passenger.title,
          Email: passenger.email,
          IdType: passenger.idProof,
          IdNumber: passenger.idProofNumber || "",
          NameOnId: `${passenger.firstName} ${passenger.lastName}`,
          Primary: index === 0,
          Ac: seat.ac || false,
          Sleeper: seat.sleeper || false,
        };
      }),
    };
  };

  const handlePayment = async () => {
    setIsProcessingPayment(true);
    const res = await loadRazorpayScript();
    if (!res) { alert("Razorpay SDK failed to load."); setIsProcessingPayment(false); return; }

    try {
      const breakdown = calculateBreakdown();
      const paymentResult = await apiService.post(`${config.MAKEPAYMENT}?Amount=${breakdown.total}&PayRequest=${paymentMethod}&CompanyId=${user?.cid}`);

      if (!paymentResult.succeeded) { alert("Payment initiation failed."); setIsProcessingPayment(false); return; }

      const { id: order_id, currency, razorpayKey, name } = paymentResult.data;
      const options = {
        key: razorpayKey, amount: breakdown.total * 100, currency, name,
        description: `Bus Booking - ${bus.operatorName}`,
        order_id,
        handler: async function (paymentResponse) {
          try {
            const bookRequest = buildBookingRequest();
            bookRequest.PaymentReference = { paymentId: paymentResponse.razorpay_payment_id, orderId: paymentResponse.razorpay_order_id, signature: paymentResponse.razorpay_signature };
            const bookResponse = await apiService.post(config.BOOKBUSSEATS, bookRequest);
            if (bookResponse && bookResponse.apiStatus?.success) {
              navigateToConfirmation("CONFIRMED", { ...bookResponse, paymentId: paymentResponse.razorpay_payment_id, message: "Booking confirmed successfully!" });
            } else {
              navigateToConfirmation("PAYMENT_SUCCESS_BOOKING_FAILED", { errorMessage: bookResponse?.apiStatus?.message });
            }
          } catch (error) { navigateToConfirmation("PAYMENT_SUCCESS_BOOKING_PENDING"); }
        },
        prefill: { email: passengerDetails[0]?.email, contact: passengerDetails[0]?.phone, name: `${passengerDetails[0]?.firstName} ${passengerDetails[0]?.lastName}` },
        theme: { color: "#1a3c2e" },
      };
      new window.Razorpay(options).open();
    } catch (error) { alert("Payment error."); setIsProcessingPayment(false); }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) { resolve(true); return; }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const navigateToConfirmation = (status, bookingData = {}) => {
    const breakdown = calculateBreakdown();
    const confirmationData = {
      bus, searchQuery, selectedSeats, passengerCount, passengerDetails, selectedBoardingPoint, selectedDroppingPoint,
      totalAmount: breakdown.total, baseFare: breakdown.baseFare, convenienceFee: breakdown.convenienceFee, bookingStatus: status, ...bookingData,
    };
    if (mode === "builder") onNavigate({ name: "bus-booking-confirmation", params: confirmationData });
    else navigate(mode === "preview" ? `/template-preview/${templateId}/bus-booking-confirmation` : "/template/bus-booking-confirmation", { state: confirmationData });
  };

  const breakdown = calculateBreakdown();

  return (
    <div className={styles.wrapper} style={themeVariables}>
      <div className={styles.mainContent}>
        <div className={styles.container}>
          <div className={styles.pageHeader}>
            <div>
              <h1 className={styles.pageTitle}>Complete Your Booking</h1>
              <p className={styles.pageSubtitle}>Review details and proceed to secure payment</p>
            </div>
            <button className={styles.backButton} onClick={() => mode === "builder" ? onNavigate({ name: "bus-seats", params: { bus, searchQuery } }) : navigate(-1)}>
              <RiArrowLeftLine /> Back
            </button>
          </div>

          <div className={styles.contentGrid}>
            <div className={styles.mainColumn}>
              <div className={styles.card}>
                <div className={styles.goldLine}></div>
                <div className={styles.cardHeader}>
                  <RiBusLine className={styles.cardIcon} />
                  <h3>Journey Details</h3>
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.journeyGrid}>
                    <div className={styles.journeyItem}><span className={styles.journeyLabel}>Route</span><span className={styles.journeyValue}>{searchQuery.fromStation} → {searchQuery.toStation}</span></div>
                    <div className={styles.journeyItem}><span className={styles.journeyLabel}>Date</span><span className={styles.journeyValue}>{formatDate(searchQuery.travelDate)}</span></div>
                    <div className={styles.journeyItem}><span className={styles.journeyLabel}>Operator</span><span className={styles.journeyValue}>{bus.operatorName}</span></div>
                    <div className={styles.journeyItem}><span className={styles.journeyLabel}>Departure</span><span className={styles.journeyValue}>{formatTime(bus.departureTime)}</span></div>
                    <div className={styles.journeyItem}><span className={styles.journeyLabel}>Arrival</span><span className={styles.journeyValue}>{formatTime(bus.arrivalTime)}</span></div>
                    <div className={styles.journeyItem}><span className={styles.journeyLabel}>Type</span><span className={styles.journeyValue}>{bus.busType}</span></div>
                  </div>
                </div>
              </div>

              <div className={styles.card}>
                <div className={styles.goldLine}></div>
                <div className={styles.cardHeader}>
                  <RiUserLine className={styles.cardIcon} />
                  <h3>Passenger Details ({passengerCount})</h3>
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.tableWrapper}>
                    <table className={styles.passengerTable}>
                      <thead>
                        <tr>
                          <th>Passenger</th>
                          <th>Seat</th>
                          <th>Age</th>
                          <th>Contact Info</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedSeats.map((seat, index) => {
                          const p = passengerDetails[index] || passengerDetails[0];
                          return (
                            <tr key={seat.id}>
                              <td><strong>{p.title} {p.firstName} {p.lastName}</strong></td>
                              <td><span className={styles.seatBadge}>{seat.id}</span></td>
                              <td>{p.age} Yrs</td>
                              <td><div>{p.phone}</div><small className={styles.textMuted}>{p.email}</small></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className={styles.card}>
                <div className={styles.goldLine}></div>
                <div className={styles.cardHeader}>
                  <RiMapPinLine className={styles.cardIcon} />
                  <h3>Boarding & Dropping Points</h3>
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.pointsGrid}>
                    <div className={styles.pointCard}>
                      <h4 className={styles.pointTitle}>Boarding Point</h4>
                      <p className={styles.pointLocation}><strong>{selectedBoardingPoint?.location}</strong></p>
                      <p className={styles.pointTime}><RiTimeLine /> {formatTime(selectedBoardingPoint?.time)}</p>
                    </div>
                    <div className={styles.pointCard}>
                      <h4 className={styles.pointTitleDropping}>Dropping Point</h4>
                      <p className={styles.pointLocation}><strong>{selectedDroppingPoint?.location}</strong></p>
                      <p className={styles.pointTime}><RiTimeLine /> {formatTime(selectedDroppingPoint?.time)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.sidebar}>
              <div className={styles.paymentCard}>
                <div className={styles.paymentHeader}>
                  <h3>Booking Summary</h3>
                </div>
                <div className={styles.paymentBody}>
                  <div className={styles.fareBreakdown}>
                    <div className={styles.fareItem}><span>Bus Fare</span><span>{formatCurrency(breakdown.baseFare)}</span></div>
                    <div className={styles.fareItem}>
                      <div><span>Conv. Fee</span><small className={styles.fareNote}>₹100 per seat</small></div>
                      <span>{formatCurrency(breakdown.convenienceFee)}</span>
                    </div>
                    <div className={styles.fareDivider}></div>
                    <div className={`${styles.fareItem} ${styles.total}`}>
                      <span>Total Amount</span>
                      <span className={styles.totalAmount}>{formatCurrency(breakdown.total)}</span>
                    </div>
                  </div>

                  <div className={styles.paymentMethodSection}>
                    <button className={styles.paymentMethodButton} onClick={() => setShowPaymentMethods(!showPaymentMethods)}>
                      <RiShieldLine />
                      <span>Method: {paymentMethod}</span>
                      <span className={styles.paymentMethodValue}>Change</span>
                    </button>
                    {showPaymentMethods && (
                      <div className={styles.paymentMethodsDropdown}>
                        <label className={styles.radioOption}><input type="radio" value="RAZORPAY" checked={paymentMethod === "RAZORPAY"} onChange={(e) => setPaymentMethod(e.target.value)} /><span>Razorpay / Cards / UPI</span></label>
                        <label className={styles.radioOption}><input type="radio" value="WALLET" checked={paymentMethod === "WALLET"} onChange={(e) => setPaymentMethod(e.target.value)} /><span>Wallet Balance</span></label>
                      </div>
                    )}
                  </div>

                  <button className={styles.payButton} onClick={handlePayment} disabled={isProcessingPayment}>
                    {isProcessingPayment ? <span className={styles.spinner}></span> : <RiLockUnlockLine />}
                    {isProcessingPayment ? "Processing..." : `Pay ${formatCurrency(breakdown.total)}`}
                  </button>

                  <div className={styles.securityBadge}><RiShieldLine /> <span>Secure & Encrypted</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusPayment2;